// Save payloads. Kept pure so the v1 create body stays provable by test.

import { format } from 'date-fns'
import dayjs from 'dayjs'

import { TIME_FORMAT } from 'common/constants/timeFormat'
import type { EHRAppointmentFormType } from 'features/Home/schemas/infoNewPatientSchema'
import type { ScheduleAppointmentDto } from 'features/Schedule/api/schedule.types'

const toClock = (value: Date): string => format(value, TIME_FORMAT.TIME_PICKER_HH_SS)

const endOf = (start: Date, duration: number): Date =>
  dayjs(start).add(duration, 'minutes').toDate()

export type CreateExtras = {
  issueIds: readonly number[]
  complaints: readonly string[]
}

export const buildCreateDto = (
  values: EHRAppointmentFormType,
  { issueIds, complaints }: CreateExtras,
): ScheduleAppointmentDto => ({
  room: values.room,
  patient: {
    id: values.id,
    first_name: values.first_name,
    last_name: values.last_name,
    phone: values.phone,
    language_type_code: values.language || values.language_type_code,
  },
  consultation_staff: values.consultation_staff,
  temperature_bypass: values.temperature_bypass ? 1 : 0,
  start_time: toClock(values.time),
  end_time: toClock(endOf(values.time, values.duration)),
  category_type_code: values.category_type_code,
  title: values.title,
  comments: values.comments,
  status_type_code: values.patient_status,
  punctuality_type_code: Number(values.punctuality_type_code),
  punctuality_minutes: values.punctuality_minutes,
  ...(issueIds.length ? { issue_ids: [...issueIds] } : {}),
  ...(complaints.length
    ? { chief_complaints: complaints.map((complaint) => ({ complaint })) }
    : {}),
})

export const buildUpdateDto = (
  values: EHRAppointmentFormType,
  patientId: number | undefined,
): ScheduleAppointmentDto & { date: string } => ({
  date: dayjs(values.date).format('YYYY-MM-DD'),
  room: values.room,
  patient: {
    id: patientId,
    first_name: values.first_name,
    last_name: values.last_name,
    phone: values.phone,
    language_type_code: values.language_type_code,
  },
  consultation_staff: values.consultation_staff,
  temperature_bypass: values.temperature_bypass ? 1 : 0,
  start_time: toClock(values.time),
  end_time: toClock(endOf(values.time, values.duration)),
  category_type_code: values.category_type_code,
  title: values.title,
  comments: values.comments,
  status_type_code: values.patient_status,
  punctuality_type_code: Number(values.punctuality_type_code),
  punctuality_minutes: values.punctuality_minutes,
})
